/*
  REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
*/
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, useAuth } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Loader2, MailCheck, ShieldAlert, Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [state, setState] = useState({ status: "loading", detail: null });

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const raw = location.hash || "";
    const params = new URLSearchParams(raw.replace(/^#/, ""));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google", { session_id: sessionId });
        // httpOnly cookie is already set by the backend — no client-side token storage.
        setUser(data);
        toast.success("Signed in with Google");
        // Clear the fragment before navigating so a refresh doesn't retry
        window.history.replaceState(null, "", window.location.pathname);
        nav("/dashboard", { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const code = typeof detail === "object" ? detail?.code : null;
        if (code === "no_account") {
          window.history.replaceState(null, "", window.location.pathname);
          nav("/request-access", {
            replace: true,
            state: { email: detail.email, name: detail.name },
          });
          return;
        }
        if (code === "pending_approval" || code === "access_request_pending") {
          setState({
            status: "pending",
            detail: detail?.message || "Your account is awaiting admin approval.",
          });
          return;
        }
        setState({
          status: "error",
          detail:
            (typeof detail === "string" && detail) ||
            detail?.message ||
            "Sign-in failed. Please try again.",
        });
      }
    })();
  }, [location.hash, nav, setUser]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#F8F9FA] px-6">
      <div className="w-full max-w-md text-center space-y-6" data-testid="auth-callback-panel">
        <div className="flex justify-center"><BrandLogo size="md" /></div>

        {state.status === "loading" && (
          <div className="space-y-4" data-testid="auth-callback-loading">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#0B132B]/5 grid place-items-center">
              <Loader2 size={28} className="text-[#0B132B] animate-spin" />
            </div>
            <h2 className="font-display text-2xl font-black text-[#0B132B]">Signing you in…</h2>
            <p className="text-sm text-[#6C757D]">Verifying your Google account.</p>
          </div>
        )}

        {state.status === "pending" && (
          <div className="space-y-4" data-testid="auth-callback-pending">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/15 grid place-items-center">
              <Clock size={28} className="text-[#D4AF37]" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
                Awaiting approval
              </div>
              <h2 className="font-display text-2xl font-black text-[#0B132B] mt-2">
                Pending admin approval
              </h2>
              <p className="text-sm text-[#6C757D] mt-3 leading-relaxed">{state.detail}</p>
            </div>
            <Button
              onClick={() => nav("/login")}
              className="bg-[#0B132B] hover:bg-[#1C2541]"
              data-testid="auth-callback-back-to-login"
            >
              Back to sign in <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </div>
        )}

        {state.status === "error" && (
          <div className="space-y-4" data-testid="auth-callback-error">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 grid place-items-center">
              <ShieldAlert size={28} className="text-red-600" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-black text-[#0B132B]">Sign-in failed</h2>
              <p className="text-sm text-[#6C757D] mt-3">{state.detail}</p>
            </div>
            <div className="flex items-center gap-3 justify-center">
              <Button variant="outline" onClick={() => nav("/login")} data-testid="auth-callback-retry">
                Back to sign in
              </Button>
              <Button
                onClick={() => nav("/request-access")}
                className="bg-[#0B132B] hover:bg-[#1C2541]"
                data-testid="auth-callback-request-access"
              >
                <MailCheck size={14} className="mr-1.5" /> Request access
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
